import React, {useEffect} from 'react';
import koMsg from './lang/ko.json';
import {IntlProvider} from 'react-intl';
import {BrowserRouter, Route, Routes} from 'react-router-dom';
import GlobalStyles from 'styles/GlobalStyle';
import Login from 'pages/Login';
import Register from 'pages/Register';
import ResetPassword from 'pages/ResetPassword';
import Main from 'pages/Main';
import Notice from 'pages/Notice';
import Usage from 'pages/Usage';
import OnSiteCreationRequest from 'pages/fieldManagement/OnSiteCreationRequest';
import PaymentFailure from 'pages/paymentManagement/PaymentFailure';
import PaymentFailureTongPlace from 'pages/paymentManagement/PaymentFailureTongPlace';
import {QueryClient, QueryClientProvider} from '@tanstack/react-query';
import SystemCheck from 'pages/noticeAndInspection/SystemCheck';
import OnSiteNotice from 'pages/noticeAndInspection/OnSiteNotice';
import SiteStateManagement from 'pages/fieldManagement/SiteStateManagement';
import UserManagementTongPlace from 'pages/userManagement/UserManagementTongPlace';
import UserManagementTongPlaceDetail from 'pages/userManagement/UserManagementTongPlaceDetail';
import UserManagementUser from 'pages/userManagement/UserManagementUser';
import UserManagementUserDetail from 'pages/userManagement/UserManagementUserDetail';
import RegisterSales from 'pages/salesManagement/RegisterSales';
import ReferralCodeList from 'pages/referralCode/ReferralCodeList';
import SalesManagement from 'pages/salesManagement/SalesManagement';
import SalesManagerDetail from 'pages/salesManagement/SalesManagerDetail';
import SalesManagerEdit from 'pages/salesManagement/SalesManagerEdit';
import PartnerCompany from 'pages/partnerCompany/PartnerCompany';
import ContractorManagement from 'pages/contractorManagement/ContractorManagement';
import ContractorStatistics from 'pages/contractorStatistics/ContractorStatistics';
import HeadquartersAccessControl from 'pages/headquartersAccessControl/HeadquartersAccessControl';
import ChangeHistory from 'pages/changeHistory/ChangeHistory';
import OperationsAnalytics from 'pages/operationsAnalytics/OperationsAnalytics';
import OperationsAnalyticsSites from 'pages/operationsAnalytics/OperationsAnalyticsSites';
import ProtectedRoute from 'components/common/ProtectedRoute';
import ErrorBoundary from 'components/common/ErrorBoundary';
import {Navigate} from 'react-router-dom';

const queryClient = new QueryClient();

function App() {
  // 언어 설정
  const userLang: string = navigator.language;

  const mapping = (userLang: string): 'ko-KR' => {
    if (userLang === 'ko-KR') return 'ko-KR';
    return 'ko-KR';
  };
  const messages: {[index: string]: string} = {
    'ko-KR': koMsg,
  }[mapping(userLang)];

  function setMobileHeight() {
    const vh = window.innerHeight * 0.01;
    document.documentElement.style.setProperty('--vh', `${vh}px`);
  }

  useEffect(() => {
    if (process.env.NODE_ENV === 'production') {
      // eslint-disable-next-line @typescript-eslint/no-empty-function
      console.log = () => {};
    }
    setMobileHeight();
    window.addEventListener('resize', setMobileHeight);
    console.log('process.env.NODE_ENV', process.env.NODE_ENV);
    console.log(
      'process.env.REACT_APP_NODE_ENV',
      process.env.REACT_APP_NODE_ENV,
    );

    return () => window.removeEventListener('resize', setMobileHeight);
  }, []);

  return (
    <IntlProvider locale={userLang} messages={messages}>
      <QueryClientProvider client={queryClient}>
        <BrowserRouter
          future={{
            v7_startTransition: true,
            v7_relativeSplatPath: true,
          }}>
          <GlobalStyles />
          <ErrorBoundary>
            <Routes>
              {/* Public routes */}
              <Route path="/" element={<Login />} />
              <Route path="/register" element={<Register />} />
              <Route path="/resetPassword" element={<ResetPassword />} />

              {/* Protected routes */}
              <Route
                path="/main"
                element={
                  <ProtectedRoute>
                    <Main />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/notice"
                element={
                  <ProtectedRoute>
                    <Notice />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/usage"
                element={
                  <ProtectedRoute>
                    <Usage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/onSiteCreationRequest"
                element={
                  <ProtectedRoute>
                    <OnSiteCreationRequest />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/paymentFailure"
                element={
                  <ProtectedRoute>
                    <PaymentFailure />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/paymentFailureTongPlace"
                element={
                  <ProtectedRoute>
                    <PaymentFailureTongPlace />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/systemCheck"
                element={
                  <ProtectedRoute>
                    <SystemCheck />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/onSiteNotice"
                element={
                  <ProtectedRoute>
                    <OnSiteNotice />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/siteStateManagement"
                element={
                  <ProtectedRoute>
                    <SiteStateManagement />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/userManagementTongPlace"
                element={
                  <ProtectedRoute>
                    <UserManagementTongPlace />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/userManagementTongPlaceDetail"
                element={
                  <ProtectedRoute>
                    <UserManagementTongPlaceDetail />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/userManagementUser"
                element={
                  <ProtectedRoute>
                    <UserManagementUser />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/userManagementUserDetail"
                element={
                  <ProtectedRoute>
                    <UserManagementUserDetail />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/registerSales"
                element={
                  <ProtectedRoute>
                    <RegisterSales />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/referralCodeList"
                element={
                  <ProtectedRoute>
                    <ReferralCodeList />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/salesManagement"
                element={
                  <ProtectedRoute>
                    <SalesManagement />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/salesManagerDetail"
                element={
                  <ProtectedRoute>
                    <SalesManagerDetail />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/salesManagerEdit"
                element={
                  <ProtectedRoute>
                    <SalesManagerEdit />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/partnerCompany"
                element={
                  <ProtectedRoute>
                    <PartnerCompany />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/contractorManagement"
                element={
                  <ProtectedRoute>
                    <ContractorManagement />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/contractorStatistics"
                element={
                  <ProtectedRoute>
                    <ContractorStatistics />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/headquartersAccessControl"
                element={
                  <ProtectedRoute>
                    <HeadquartersAccessControl />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/changeHistory"
                element={
                  <ProtectedRoute>
                    <ChangeHistory />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/operationsAnalytics"
                element={
                  <ProtectedRoute>
                    <OperationsAnalytics />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/operationsAnalytics/sites"
                element={
                  <ProtectedRoute>
                    <OperationsAnalyticsSites />
                  </ProtectedRoute>
                }
              />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </ErrorBoundary>
        </BrowserRouter>
      </QueryClientProvider>
    </IntlProvider>
  );
}

export default App;
